"use client";

import { permalinkPath, toInternalPath } from "@tripcart/shared";
import { useEffect, useRef, useState } from "react";
import Link, { useLinkStatus } from "next/link";
import { usePathname } from "next/navigation";
import {
  ArrowRightIcon,
  FacebookIcon,
  InstagramIcon,
  MailIcon,
  MenuIcon,
  MessageCircleIcon,
  ShoppingBagIcon,
  YoutubeIcon,
} from "lucide-react";

import type { AgencyBrandDto, PublicMenu } from "@tripcart/shared";
import { Button } from "@tripcart/traveler-ui/components/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@tripcart/traveler-ui/components/sheet";
import { cx } from "@tripcart/traveler-ui/cx";

import { Logo } from "@/components/logo";

import { ChromeContainer } from "@/components/chrome-container";
import { NavMenuBar, NavMenuMobile } from "@/components/nav-menu";
import { PrefetchLink } from "@/components/prefetch-link.client";
import { useCartCount } from "@/lib/cart/use-cart";
import { resetScrollOnNavigate } from "@/lib/reset-scroll-on-navigate";
import { usePluralTranslator, useTranslator } from "@/lib/translation-context";

import styles from "./nav.module.css";

// ---------------------------------------------------------------------------
// Desktop nav link with animated underline
// ---------------------------------------------------------------------------
// Underline element — extracted so it can call `useLinkStatus()`, which
// must run inside a descendant of <Link>. While the click is in flight
// the underline fills to full width and pulses subtly, so the user
// sees the click registered even before the next route paints.
function NavLinkUnderline() {
  const { pending } = useLinkStatus();
  return (
    <span
      aria-hidden
      data-pending={pending ? "" : undefined}
      className={styles.underline}
    />
  );
}

// `next.config.ts` sets `trailingSlash: true`, so the rendered href and
// `usePathname()` both carry one — but `permalinkPath()`'s own return value
// does not. Comparing the raw strings never matches; strip the slash from
// both sides first.
function stripTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

function NavLink({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    // Intent-prefetched: warms this ISR target's full payload on hover / touch
    // / focus, so the click paints instantly — WITHOUT the load-time viewport
    // prefetch storm that used to pull ~20 route chunks (incl. the Clerk SDK)
    // against the LCP image. See <PrefetchLink>.
    <PrefetchLink
      href={href}
      aria-current={active ? "page" : undefined}
      className={styles.navLink}
      scroll={false}
      onClick={resetScrollOnNavigate}
    >
      {children}
      <NavLinkUnderline />
    </PrefetchLink>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface NavProps {
  agency: AgencyBrandDto;
  /** Auth-aware element slotted into the right side of the header.
   *  Rendered by a server component (`<AuthMenu />`) so the signed-in
   *  state resolves without a client flash; this component still ships
   *  as a client island for cart-count reactivity. */
  authSlot?: React.ReactNode;
  /** Language picker, slotted in only when the Google Translate app is
   *  installed (gated server-side by `<SiteChrome>`). Null otherwise. */
  localeSlot?: React.ReactNode;
  /** True when a `top` notice bar renders above the nav — shifts the fixed
   *  header down by the strip's 44px so it sits below the bar. The layout
   *  applies the matching `main` padding. */
  noticeBarAbove?: boolean;
  /** True when a `bottom` notice bar sits directly below the nav. On overlay
   *  (transparent) routes this keeps the header scrim solid to its bottom edge
   *  so the header reads full-height and hands off cleanly to the bar, instead
   *  of fading and letting the hero show through (styles/storefront.css). */
  noticeBarBelow?: boolean;
  /** The agency's first menu (feature: menu-builder), or null when the
   *  operator has built none — the stock links below render instead, which is
   *  the whole of AC-03. Replaces rather than supplements them: the first menu
   *  IS the header navigation. */
  menu?: PublicMenu | null;
}

export function Nav({
  agency,
  authSlot,
  localeSlot,
  noticeBarAbove = false,
  noticeBarBelow = false,
  menu = null,
}: NavProps) {
  const t = useTranslator();
  const tn = usePluralTranslator();

  // Tours, Destinations and Activity Types sit under the agency's permalink
  // words (feature: permalinks). Labels go through `t()` so each phrase is
  // scraped into the catalog and renders in the agency's language.
  const tourHref = permalinkPath(agency.permalinks, "tour");
  const destinationHref = permalinkPath(agency.permalinks, "destination");
  const activityHref = permalinkPath(agency.permalinks, "activity");
  const primaryLinks = [
    { label: t("Tours"), href: tourHref },
    { label: t("Destinations"), href: destinationHref },
    { label: t("Activity Types"), href: activityHref },
  ];

  const mobileGroups = [
    {
      heading: t("Browse"),
      links: [
        { label: t("Tours"), href: tourHref },
        { label: t("Destinations"), href: destinationHref },
        { label: t("Activity Types"), href: activityHref },
      ],
    },
  ];

  const [scrolled, setScrolled] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const itemCount = useCartCount();
  // Routes with a full-bleed photo hero want the header to float transparently
  // at the top, then transition to the standard glass once the user scrolls.
  // Every route whose first section is a bleed hero belongs here, and ONLY
  // those: the list is one half of a pair, the other half being that hero's
  // own negative top margin. A route in one and not the other is visibly
  // wrong — `/tour-types` and its detail pages pulled their heroes up under
  // the nav for weeks while this list did not know about them, so an opaque
  // near-white bar sat on top of the photo.
  //
  // Tour DETAIL pages (`/tours/<slug>`) are deliberately not here: they open
  // on a gallery, not a bleed hero.
  //
  // Matched on the ROUTE, not the address: under a custom permalink word the
  // address is `/treks/`, served by the `/tours/` route this checks for.
  const address = usePathname();
  const pathname = address && toInternalPath(agency.permalinks, address);
  const overlayRoute = Boolean(
    pathname &&
    (/^\/tours\/?$/.test(pathname) ||
      /^\/destinations(\/|$)/.test(pathname) ||
      /^\/tour-types(\/|$)/.test(pathname)),
  );
  const transparent = overlayRoute && !scrolled;
  const showCartBadge = itemCount > 0;

  // IntersectionObserver on a sentinel element — NO scroll listener
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setScrolled(entry ? !entry.isIntersecting : false);
      },
      { threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Sentinel — zero-height element at the top of the page */}
      <div ref={sentinelRef} aria-hidden className={styles.sentinel} />

      <header
        // Targeted by the notice-bar reclaim rules (styles/storefront.css,
        // cms-storefront-frame.tsx) instead of a bare `header`.
        data-tc-nav=""
        data-scrolled={scrolled ? "true" : undefined}
        data-transparent={transparent ? "true" : undefined}
        // The ROUTE's own shape, and so unaffected by scrolling —
        // `data-transparent` is `overlayRoute && !scrolled`, which makes it a
        // paint state, not a layout fact. `globals.css` keyed the bleed hero's
        // negative margin on it anyway, so the hero's `margin-top` flipped
        // between -104px and -64px the moment the header went opaque and every
        // page with a bottom notice bar dropped 40px on its first scroll
        // (measured 2026-09-23). Layout rules key on this one; paint rules on
        // `data-transparent`.
        data-overlay-route={overlayRoute ? "true" : undefined}
        data-notice-below={noticeBarBelow ? "true" : undefined}
        // Two modes (nav.module.css): always-on frosted glass, or fully
        // transparent over a hero until the visitor scrolls.
        className={cx(styles.header, noticeBarAbove && styles.belowNoticeBar)}
      >
        <ChromeContainer
          as="nav"
          aria-label={t("Main Navigation")}
          className={styles.bar}
        >
          {/* ----------------------------------------------------------------
              Logo / wordmark
          ---------------------------------------------------------------- */}
          <PrefetchLink
            href="/"
            // Stable hook so transparent-header mode can whiten the wordmark
            // without depending on <Brand>'s internal markup (styles/storefront.css).
            data-brand
            aria-label={t("{brand} — home", { brand: agency.name })}
            className={styles.brand}
            scroll={false}
            onClick={resetScrollOnNavigate}
          >
            <Logo agency={agency} />
          </PrefetchLink>

          {/* ----------------------------------------------------------------
              Desktop nav links
          ---------------------------------------------------------------- */}
          {menu ? (
            <NavMenuBar menu={menu} />
          ) : (
            <ul className={styles.links}>
              {primaryLinks.map((link) => (
                <li key={link.href}>
                  <NavLink
                    href={link.href}
                    active={
                      Boolean(address) &&
                      stripTrailingSlash(address) ===
                        stripTrailingSlash(link.href)
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}

          {/* ----------------------------------------------------------------
              Desktop right actions
          ---------------------------------------------------------------- */}
          <div className={styles.actions}>
            {/* Language picker (only when the Translate app is installed) */}
            {localeSlot}

            {/* Cart button — pill with count chip */}
            <PrefetchLink
              href="/cart"
              aria-label={tn(
                "Cart ({count} trip)",
                "Cart ({count} trips)",
                itemCount,
                { count: itemCount },
              )}
              className={styles.cart}
              scroll={false}
              onClick={resetScrollOnNavigate}
            >
              <ShoppingBagIcon className={styles.cartIcon} strokeWidth={1.5} />
              {showCartBadge && (
                <span className={styles.badge}>{itemCount}</span>
              )}
            </PrefetchLink>

            {/* Auth-aware slot (Log in / Sign up or avatar menu) */}
            {authSlot ? (
              <div className={styles.authSlot}>{authSlot}</div>
            ) : null}

            {/* Primary CTA */}
            <Button
              size="default"
              className={styles.cta}
              render={
                <PrefetchLink
                  href="/contact"
                  scroll={false}
                  onClick={resetScrollOnNavigate}
                />
              }
            >
              {t("Plan Your Trip")}
              <ArrowRightIcon className={styles.ctaArrow} strokeWidth={2} />
            </Button>
          </div>

          {/* ----------------------------------------------------------------
              Mobile hamburger
          ---------------------------------------------------------------- */}
          <div className={styles.mobileOnly}>
            <Sheet>
              <SheetTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("Open Navigation Menu")}
                    className={styles.menuTrigger}
                  >
                    <MenuIcon className={styles.menuIcon} strokeWidth={1.5} />
                  </Button>
                }
              />

              <SheetContent size="sm">
                {/* Sheet header with agency logo */}
                <SheetHeader>
                  <div className={styles.sheetBar}>
                    <SheetClose
                      nativeButton={false}
                      render={
                        <Link
                          href="/"
                          aria-label={t("{brand} — home", {
                            brand: agency.name,
                          })}
                          className={styles.sheetBrand}
                          scroll={false}
                          onClick={resetScrollOnNavigate}
                        >
                          <Logo agency={agency} />
                        </Link>
                      }
                    />
                    <div className={styles.sheetActions}>
                      {localeSlot}
                      <SheetClose
                        nativeButton={false}
                        render={
                          <Link
                            href="/cart"
                            aria-label={tn(
                              "Cart ({count} trip)",
                              "Cart ({count} trips)",
                              itemCount,
                              { count: itemCount },
                            )}
                            className={styles.sheetCart}
                            scroll={false}
                            onClick={resetScrollOnNavigate}
                          >
                            <ShoppingBagIcon
                              className={styles.cartIcon}
                              strokeWidth={1.5}
                            />
                            {showCartBadge && (
                              <span
                                className={cx(styles.badge, styles.sheetBadge)}
                              >
                                {itemCount}
                              </span>
                            )}
                          </Link>
                        }
                      />
                    </div>
                  </div>
                  <SheetTitle className="tui-sr-only">
                    {t("{brand} navigation", { brand: "Tripcart" })}
                  </SheetTitle>
                </SheetHeader>

                {/* Nav groups */}
                <nav
                  aria-label={t("Mobile Navigation")}
                  className={styles.mobileNav}
                >
                  {menu ? (
                    <NavMenuMobile menu={menu} t={t} />
                  ) : (
                    mobileGroups.map((group) => (
                      <div key={group.heading}>
                        <p className={styles.metaLabel}>{group.heading}</p>
                        <ul className={styles.groupList}>
                          {group.links.map((link) => (
                            <li key={link.href}>
                              <SheetClose
                                nativeButton={false}
                                render={
                                  <PrefetchLink
                                    href={link.href}
                                    className={styles.mobileLink}
                                    scroll={false}
                                    onClick={resetScrollOnNavigate}
                                  >
                                    {link.label}
                                  </PrefetchLink>
                                }
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  )}

                  {authSlot ? (
                    <div className={styles.mobileSection}>
                      <p className={styles.metaLabel}>{t("Account")}</p>
                      {/* authSlot's own link carries its own inline padding;
                          offset it so "Log in" aligns with the browse links
                          above (not double-indented). */}
                      <div className={styles.mobileAuth}>{authSlot}</div>
                    </div>
                  ) : null}

                  <div className={styles.mobileSection}>
                    <SheetClose
                      nativeButton={false}
                      render={
                        <Button
                          size="lg"
                          className={styles.mobileCta}
                          render={
                            <Link
                              href="/contact"
                              scroll={false}
                              onClick={resetScrollOnNavigate}
                            />
                          }
                        >
                          {t("Plan Your Trip")}
                          <ArrowRightIcon
                            className={styles.mobileCtaArrow}
                            strokeWidth={2}
                          />
                        </Button>
                      }
                    />
                  </div>
                </nav>

                {/* Sheet footer — social icons + contact line */}
                {/* The footer is a contact block rather than an action row,
                    so it stacks; the band, border and padding stay the
                    component's. */}
                <SheetFooter className={styles.sheetFooter}>
                  <p className={styles.touchHeading}>{t("Get in Touch")}</p>
                  <div className={styles.socials}>
                    {agency.contact?.email && (
                      <a
                        href={`mailto:${agency.contact.email}`}
                        aria-label={t("Email")}
                        className={styles.social}
                      >
                        <MailIcon
                          className={styles.socialIcon}
                          strokeWidth={1.5}
                        />
                      </a>
                    )}
                    {agency.contact?.whatsapp && (
                      <a
                        href={`https://wa.me/${agency.contact.whatsapp.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="WhatsApp"
                        className={styles.social}
                      >
                        <MessageCircleIcon
                          className={styles.socialIcon}
                          strokeWidth={1.5}
                        />
                      </a>
                    )}
                    {agency.social?.instagram && (
                      <a
                        href={agency.social.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className={styles.social}
                      >
                        <InstagramIcon
                          className={styles.socialIcon}
                          strokeWidth={1.5}
                        />
                      </a>
                    )}
                    {agency.social?.facebook && (
                      <a
                        href={agency.social.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className={styles.social}
                      >
                        <FacebookIcon
                          className={styles.socialIcon}
                          strokeWidth={1.5}
                        />
                      </a>
                    )}
                    {agency.social?.youtube && (
                      <a
                        href={agency.social.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="YouTube"
                        className={styles.social}
                      >
                        <YoutubeIcon
                          className={styles.socialIcon}
                          strokeWidth={1.5}
                        />
                      </a>
                    )}
                  </div>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>
        </ChromeContainer>
      </header>
    </>
  );
}
